import {
  Calendar,
  CalendarEvent,
  CalendarEvents,
  DateString,
  fullDayMonthDateString,
  isShiftEvent,
  Shift,
  ShiftsGroupEvent,
  UserRole,
} from '@nextforce/api'
import { debounce } from 'lodash'
import {
  TASK_EVENT_HEIGHT,
  TASK_EVENT_TOP_MARGIN,
} from 'schedule/views/event/task'
import {
  CUSTOM_EVENT_HEIGHT,
  CUSTOM_EVENT_TOP_MARGIN,
} from '../views/event/custom_events'
import {
  HOLIDAY_EVENT_HEIGHT,
  HOLIDAY_TOP_MARGIN,
} from '../views/event/holiday'
import {
  eventHasSummary,
  eventShiftGroups,
  ShiftGroups,
} from '../views/event/shifts_group'
import {
  getShiftHeight,
  getShiftTopMargin,
} from '../views/event/shifts_group/shift'
import {
  EVENT_SUMMARY_HEIGHT,
  EVENT_SUMMARY_STACK_OFFSET,
  getCollpasedShiftCount,
} from '../views/event/shifts_group/summary'
import { getTimeOffEventsHeight } from '../views/event/time_off_requests'

export type ProcessedEvent = CalendarEvent & {
  dateString: DateString
  formattedDate: string
  calculatedHeight: number
  key: string
}

// Optimized data structures
// Height calculation with caching
// Optimized shift groups calculation with caching
// Optimized date formatting with caching
// Cache for event heights

export function calculateEventHeight(
  eventHeightCache: Map<string, number>,
  event: CalendarEvent,
  role: UserRole
): number {
  if (eventHeightCache.has(event.id)) {
    return eventHeightCache.get(event.id)!
  }

  let height = 0
  switch (event.type) {
    case 'holiday':
      height = HOLIDAY_EVENT_HEIGHT + HOLIDAY_TOP_MARGIN
      break
    case 'custom_events':
      height = CUSTOM_EVENT_HEIGHT + CUSTOM_EVENT_TOP_MARGIN
      break
    case 'tasks':
      height = TASK_EVENT_HEIGHT + TASK_EVENT_TOP_MARGIN
      break
    case 'time_off':
      const calendar = event.calendar === 'helper' ? 'helper' : 'store'
      height = getTimeOffEventsHeight(calendar, event.timeOffEvents)
      break
    case 'shifts':
    case 'open_shifts':
      const shifts = eventShiftGroups(event)
      if (shifts.normal.length > 0) {
        if (!eventHasSummary(event)) {
          height =
            getShiftHeight(event.calendar, role, shifts.normal[0], true) +
            getShiftTopMargin(false)
        } else {
          const collapsedCount = getCollpasedShiftCount(
            false,
            shifts.normal.length
          )
          height =
            EVENT_SUMMARY_HEIGHT +
            EVENT_SUMMARY_STACK_OFFSET * (collapsedCount - 1)
        }
      }
      break
    default:
      height = 100
  }

  eventHeightCache.set(event.id, height)
  return height
}

export function getStableEventId(event: ProcessedEvent | CalendarEvent) {
  // For shift events, use a combination of calendar and shift IDs to ensure stability
  if (isShiftEvent(event)) {
    const shiftIds = event.shifts
      .map(s => s.id)
      .sort()
      .join('-')
    return `${event.calendar}-${shiftIds}`
  }
  // For other events, use their natural ID
  return event.id
}

export function processEvents(
  eventHeightCache: Map<string, number>,
  events: CalendarEvents,
  role: UserRole
): ProcessedEvent[] {
  const processed: ProcessedEvent[] = []

  Object.entries(events).forEach(([dateString, dateEvents]) => {
    dateEvents.forEach(event => {
      const stableId = getStableEventId(event)
      processed.push({
        ...event,
        dateString,
        formattedDate: fullDayMonthDateString(dateString),
        calculatedHeight: calculateEventHeight(eventHeightCache, event, role),
        id: stableId,
        key: `${dateString}-${stableId}`,
      })
    })
  })

  return processed.sort((a, b) => a.dateString.localeCompare(b.dateString))
}

export const createDebouncedScroll = (callback: (date: Date) => void) =>
  debounce(callback, 100, { leading: true, trailing: false })

export const SECTION_HEADER_HEIGHT = 30

const getShiftGroupHeight = (
  shifts: Shift[],
  calendar: Calendar,
  isExpanded: boolean,
  showSummaryTiles: boolean,
  role: UserRole
) => {
  return shifts.reduce((total, shift) => {
    const height =
      getShiftHeight(calendar, role, shift, !showSummaryTiles && isExpanded) +
      getShiftTopMargin(false)
    return total + height
  }, 0)
}

const getNormalShiftsHeight = (
  shiftGroups: ShiftGroups,
  event: ShiftsGroupEvent,
  isExpanded: boolean,
  showSummaryTile: boolean,
  role: UserRole
) => {
  if (shiftGroups.normal.length === 0) return 0

  if (eventHasSummary(event)) {
    if (!isExpanded) {
      const collapsedCount = getCollpasedShiftCount(
        false,
        shiftGroups.normal.length
      )
      return EVENT_SUMMARY_HEIGHT + EVENT_SUMMARY_STACK_OFFSET * collapsedCount
    }
    const summaryHeight = showSummaryTile ? EVENT_SUMMARY_HEIGHT : 0
    return (
      summaryHeight +
      getShiftGroupHeight(
        shiftGroups.normal,
        event.calendar,
        isExpanded,
        showSummaryTile,
        role
      )
    )
  }

  return getShiftGroupHeight(
    shiftGroups.normal,
    event.calendar,
    isExpanded,
    showSummaryTile,
    role
  )
}
export const calculateShiftScrollOffset = (
  targetShift: Shift,
  processedDayEvents: ProcessedEvent[] | undefined,
  expandedEvents: Map<string, boolean>,
  showSummaryTiles: boolean,
  role: UserRole
) => {
  let offset = 0
  if (!processedDayEvents) {
    return offset
  }
  for (const event of processedDayEvents) {
    if (!isShiftEvent(event)) {
      offset += event.calculatedHeight ?? 0
      continue
    }

    const shiftGroups = eventShiftGroups(event)
    const isExpanded = expandedEvents.get(event.id) ?? !showSummaryTiles
    const shiftInEvent = event.shifts.some(s => s.id === targetShift.id)

    // If this is the target event, find the exact position
    if (shiftInEvent) {
      // Check priority shifts
      for (const shift of shiftGroups.priority) {
        if (shift.id === targetShift.id) {
          return offset
        }
        offset +=
          getShiftHeight(event.calendar, role, shift, true) +
          getShiftTopMargin(false)
      }

      // Check open shifts
      for (const shift of shiftGroups.open) {
        if (shift.id === targetShift.id) {
          return offset
        }
        offset +=
          getShiftHeight(event.calendar, role, shift, true) +
          getShiftTopMargin(false)
      }

      // Must be in normal shifts
      if (!isExpanded && eventHasSummary(event)) {
        const normalOffset =
          offset +
          getNormalShiftsHeight(
            shiftGroups,
            event,
            isExpanded,
            showSummaryTiles,
            role
          )
        return normalOffset
      }

      // Add summary height if expanded with tiles
      if (isExpanded && eventHasSummary(event) && showSummaryTiles) {
        offset += EVENT_SUMMARY_HEIGHT
      }

      // Find position in normal shifts
      for (const shift of shiftGroups.normal) {
        if (shift.id === targetShift.id) {
          return offset
        }
        offset +=
          getShiftHeight(event.calendar, role, shift, !showSummaryTiles) +
          getShiftTopMargin(false)
      }
    }

    // Not target event, add all heights
    const priorityHeight = getShiftGroupHeight(
      shiftGroups.priority,
      event.calendar,
      true,
      false,
      role
    )
    offset += priorityHeight

    const openHeight = getShiftGroupHeight(
      shiftGroups.open,
      event.calendar,
      true,
      false,
      role
    )
    offset += openHeight

    const normalHeight = getNormalShiftsHeight(
      shiftGroups,
      event,
      isExpanded,
      showSummaryTiles,
      role
    )
    offset += normalHeight
  }

  return -1 // Shift not found
}
